import { SkeletonMotion } from '@acp/core-ui';
import {
    Text,
    VStack,
    Grid,
    GridItem,
    HStack,
    Button,
    Card,
    CardBody,
    Skeleton,
    Badge,
    Tooltip,
    BorderProps,
} from '@chakra-ui/react';
import { Notice } from '@acp/shared';
import { format, formatDistanceToNow } from 'date-fns';
import { brandIcons } from '@acp/core-ui/assets';
import { PortfolioActiveNoticesTag } from '../../../portfolio';
import { ResolveActionButton } from '../../../active-notice/components/buttons/resolve-action-button';
import { useActiveNotices } from '../../../active-notice/hooks/use-active-notices';
import { RowLayout } from '../../../active-notice/components/cards/active-notice-row-layout';

const gap = 4;

function GridWrapper({ children }: { children: React.ReactNode }) {
    return (
        <Grid
            gridTemplateRows="auto"
            gridTemplateAreas={[`
            "type type"
            "address address"
            "active-notice active-notice"
            "actions actions"
        `, `
            "type type type"
            "address address address"
            "active-notice active-notice actions"
        `, null, `
            "type address active-notice actions"
        `]}
            gridTemplateColumns={['1fr 1fr', '1fr 1fr 1fr', null, '3fr 5fr 5fr 3fr']}
            gap={0}
            alignItems="center"
            justifyItems="start"
            h="auto"
        >
            {children}
        </Grid>
    );
}

export function UrgentActiveNoticeRowSkeleton() {
    return (
        <Card
            borderWidth={1}
            borderColor="muted.50"
            bg="white"
            variant="filled"
            w="full"
            p={5}
        >
            <CardBody as={GridWrapper} p={0} alignItems="center" justifyItems="center">
                <GridItem as={VStack} gap={1} gridArea="type" mb={[gap, null, null, 0]} alignItems="start">
                    <Skeleton>
                        <Text>Access Issue</Text>
                    </Skeleton>
                </GridItem>
                <VStack
                    alignItems="start"
                    gap={1}
                    w={52}
                    gridArea="address"
                    mb={[gap, null, null, 0]}
                >
                    <SkeletonMotion height={5} />
                    <SkeletonMotion height={5} />
                </VStack>

                <GridItem area="active-notice">
                    <VStack align="left" gap={1}>
                        <SkeletonMotion>
                            <Badge>Access issue</Badge>
                        </SkeletonMotion>
                        <Skeleton>
                            <Text fontSize="xs">
                                Added about X days ago
                            </Text>
                        </Skeleton>
                    </VStack>
                </GridItem>

                <GridItem as={HStack} gridArea="actions" justifyContent="end" width="100%" gap={3} alignSelf={['center']}>
                    <Skeleton>
                        <Button
                            borderRightWidth={1}
                            borderRightColor="secondary.900"
                            color="secondary.25"
                            size="sm"
                        >
                            actions menu xx
                        </Button>
                    </Skeleton>
                </GridItem>
            </CardBody>
        </Card>
    );
}

export function UrgentActiveNoticeRow(
    {
        notice,
        gridWrapper = GridWrapper,
        borderLeftColor = 'secondary.500',
        isHideAddress = false,
    }:
    {
        notice: Notice;
        gridWrapper?: React.ComponentType<{ children: React.ReactNode }>;
        borderLeftColor?: BorderProps['borderLeftColor'];
        isHideAddress?: boolean;
    }
) {
    const { property } = notice;

    const {
        activeNotice,
    } = useActiveNotices(
        notice,
        {
            propertyId: property.compliancePropertyId,
            address: property.address,
            isIntegrated: property.isIntegrated,
        }
    );

    if (activeNotice === null) return null;

    return (
        <RowLayout borderLeftColor={borderLeftColor}>
            <RowLayout.Body as={gridWrapper} p={0} alignItems="center" justifyItems="center" rowGap={[2, null, null, 0]}>
                <GridItem as={HStack} gap={2} area="type" mb={[gap, null, null, 0]}>
                    <RowLayout.Icon imgSrc={brandIcons.activeNoticeSmMuted} height="35px" />
                    <VStack gap={0} alignItems="start">
                        <Text fontWeight="medium">Access Issue</Text>
                    </VStack>
                </GridItem>

                {!isHideAddress && (
                    <GridItem area="address" mb={[gap, null, null, 0]}>
                        <RowLayout.Address property={property} tags={notice.tags} />
                    </GridItem>
                )}

                <GridItem area="active-notice">
                    <VStack alignItems="start" gap={1}>
                        <PortfolioActiveNoticesTag
                            property={property}
                            isIntegrated={property.isIntegrated}
                            popoverPlacement="auto"
                            address={property.addressLine1}
                            notice={notice}
                            useFullTagDescription={false}
                            hideResolveButton
                        />
                        <Tooltip label={`Notified on ${format(notice.createdAt, 'dd/MM/yyyy')}`} bgColor="white" color="black">
                            <Text fontSize="xs" fontWeight="bold" cursor="default" color="muted.500">
                                {`Notified ${formatDistanceToNow(notice.createdAt, { addSuffix: true })}`}
                            </Text>
                        </Tooltip>
                    </VStack>
                </GridItem>

                <GridItem
                    as={HStack}
                    area="actions"
                    justifyContent={['start', 'end']}
                    pt={[5, 'initial']}
                    textAlign={['left', 'right']}
                    width="100%"
                    gap={3}
                    alignSelf={['center', 'end', null, 'center']}
                >
                    <ResolveActionButton
                        property={property}
                        notice={notice}
                        tenancyId={null}
                    />
                </GridItem>
            </RowLayout.Body>
        </RowLayout>
    );
}
